import { Chat, IChat } from '@app/models/Chat'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IMessagesListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IChat>
  readonly showArchived: boolean
  readonly totalItems: number
}

const MessagesListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<Chat>(),
  showArchived: false,
  totalItems: 0,
})

export class MessagesListState extends MessagesListStateRecord
  implements IMessagesListState {
  public static fromJS(initialState?: Partial<IMessagesListState>) {
    if (!initialState) {
      return new MessagesListState()
    }

    const { items, ...rest } = initialState
    return new MessagesListState({
      items: items ? List(items.map((i) => Chat.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<Chat>
  public readonly showArchived: boolean
  public readonly totalItems: number

  constructor(props?: Partial<MessagesListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum MessagesListActionTypes {
  LoadMessages = 'MESSAGES_LIST::LOAD_MESSAGES',
  LoadMessagesFailure = 'MESSAGES_LIST::LOAD_MESSAGES_FAILURE',
  LoadMessagesRequest = 'MESSAGES_LIST::LOAD_MESSAGES_REQUEST',
  LoadMessagesSuccess = 'MESSAGES_LIST::LOAD_MESSAGES_SUCCESS',
  SetFilterText = 'MESSAGES_LIST::SET_FILTER_TEXT',
  SetShowArchived = 'MESSAGES_LIST::SET_SHOW_ARCHIVED',
}

interface ILoadMessagesAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: MessagesListActionTypes.LoadMessages
}

interface ILoadMessagesFailureAction {
  error: string
  type: MessagesListActionTypes.LoadMessagesFailure
}

interface ILoadMessagesRequestAction {
  type: MessagesListActionTypes.LoadMessagesRequest
}

interface ILoadMessagesSuccessAction {
  append: boolean
  items: Chat[]
  totalItems: number
  type: MessagesListActionTypes.LoadMessagesSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: MessagesListActionTypes.SetFilterText
}

interface ISetShowArchivedAction {
  archived: boolean
  type: MessagesListActionTypes.SetShowArchived
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadMessagesFailureAction
  | ILoadMessagesRequestAction
  | ILoadMessagesSuccessAction
  | ISetFilterTextAction
  | ISetShowArchivedAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadMessages(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadMessagesAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: MessagesListActionTypes.LoadMessages,
  }
}

function loadMessagesFailure(error: string): ILoadMessagesFailureAction {
  return {
    error,
    type: MessagesListActionTypes.LoadMessagesFailure,
  }
}

function loadMessagesRequest(): ILoadMessagesRequestAction {
  return {
    type: MessagesListActionTypes.LoadMessagesRequest,
  }
}

function loadMessagesSuccess(
  items: Chat[],
  append: boolean,
  totalItems: number,
): ILoadMessagesSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: MessagesListActionTypes.LoadMessagesSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: MessagesListActionTypes.SetFilterText,
  }
}

function setShowArchived(archived: boolean): ISetShowArchivedAction {
  return {
    archived,
    type: MessagesListActionTypes.SetShowArchived,
  }
}

export const actionCreators = {
  loadMessages,
  setFilterText,
  setShowArchived,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<MessagesListState> = (
  state: MessagesListState = new MessagesListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case MessagesListActionTypes.LoadMessagesFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as MessagesListState

    case MessagesListActionTypes.LoadMessagesRequest:
      return state.set('isLoading', true) as MessagesListState

    case MessagesListActionTypes.LoadMessagesSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Chat>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as MessagesListState

    case MessagesListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as MessagesListState

    case MessagesListActionTypes.SetShowArchived:
      return state.set('showArchived', action.archived) as MessagesListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadMessagesSaga(action: ILoadMessagesAction) {
  try {
    yield put(loadMessagesRequest())

    const currentState: MessagesListState = yield select(
      (state: ApplicationState) => state.messageList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = currentState.showArchived
      ? '/api/Messages/All'
      : '/api/Messages'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Chat[] = result.results.map((c: any) => Chat.fromJS(c))
      yield put(
        loadMessagesSuccess(contacts, action.append, result.paging.total),
      )
    } else {
      yield put(loadMessagesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadMessagesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadMessages() {
  yield takeLatest(MessagesListActionTypes.LoadMessages, loadMessagesSaga)
}

export function* messageListRootSaga() {
  yield fork(watchLoadMessages)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): MessagesListState =>
  state.messageList

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectShowArchived = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.showArchived,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
